/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

export const SkipRetry = new HttpContextToken<boolean>(() => false);

export const ForceRetry = new HttpContextToken<boolean>(() => false);

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  /** Total retry attempts AFTER the initial try (so user sees 1+2 = 3 total). */
  private readonly MAX_RETRIES = 2;

  /** Base delay in ms; actual delay = BASE_DELAY_MS * 2^(attempt - 1). */
  private readonly BASE_DELAY_MS = 300;

  /** Methods considered safe to retry without user opt-in. */
  private readonly IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req.context.get(SkipRetry)) {
      return next.handle(req);
    }

    const method = (req.method || '').toUpperCase();
    const isIdempotent = this.IDEMPOTENT_METHODS.has(method);
    const forceRetry = req.context.get(ForceRetry);

    if (!isIdempotent && !forceRetry) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error: HttpErrorResponse, index: number) => {
            const attempt = index + 1;

            // Only retry transient fetch-layer failures (no HTTP response).
            const isTransient =
              error instanceof HttpErrorResponse && error.status === 0;

            if (!isTransient || attempt > this.MAX_RETRIES) {
              return throwError(() => error);
            }

            const delay = this.BASE_DELAY_MS * Math.pow(2, attempt - 1);
            return timer(delay);
          })
        )
      )
    );
  }
}
